import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import {
  SITE_NAME, SITE_URL,
  SUPPORTED_LANGS, HREFLANG_MAP, alternatesFor,
} from '../../lib/seo/metadata';

/** Attribut posé sur les <script> JSON-LD du <head>, par le serveur ET ici. */
const JSONLD_ATTR = 'data-seo-jsonld';

/**
 * Pose les JSON-LD de la page dans le <head>, en DOM direct.
 *
 * Pourquoi pas via Helmet : react-helmet-async 2.0.5 sous React 19
 * n'applique pas ses enfants <script> (mesuré le 02/09/2026 sur /plans :
 * zéro script rendu, les deux Product déclarés n'existaient nulle part).
 * Le serveur injecte les mêmes blocs dans le HTML brut (cf. serve_spa,
 * depuis le manifeste), avec le même attribut et le même chemin : à
 * l'hydratation on retrouve les nôtres et on ne touche à rien ; en
 * navigation SPA on remplace ceux de la page précédente.
 */
function syncHeadJsonLd(pathKey, docs) {
  if (typeof document === 'undefined') return;
  const head = document.head;
  const wanted = docs.map((d) => JSON.stringify(d));
  const existing = [...head.querySelectorAll(`script[${JSONLD_ATTR}]`)];
  const keep = new Set();
  for (const el of existing) {
    const idx = wanted.indexOf(el.textContent);
    if (el.getAttribute(JSONLD_ATTR) === pathKey && idx !== -1 && !keep.has(idx)) {
      keep.add(idx);
    } else {
      el.remove();
    }
  }
  wanted.forEach((json, idx) => {
    if (keep.has(idx)) return;
    const el = document.createElement('script');
    el.type = 'application/ld+json';
    el.setAttribute(JSONLD_ATTR, pathKey);
    el.textContent = json;
    head.appendChild(el);
  });
}

/**
 * Reusable SEO head. Wraps react-helmet-async.
 *
 * Props:
 *   metadata   — object produced by generateMetadata() (required)
 *
 * The metadata object drives every side of the page's head:
 *   - <title> + description
 *   - canonical link
 *   - Open Graph + Twitter card
 *   - hreflang alternates (built from metadata.alternates when present,
 *     defaulting to "same URL for every supported language")
 *   - JSON-LD (one or many @graph-style documents)
 *   - <html lang="fr" | "nl" | "en"> derived from metadata.locale
 *
 * If `metadata` is missing the component silently renders nothing —
 * pages that don't have a catalog entry keep working, they just won't
 * emit richer head tags.
 */
export default function PageHead({ metadata }) {
  const {
    title, description, canonical, ogImage, ogType, locale, noindex,
    article, jsonLd, ogTitle, ogDescription,
  } = metadata || {};

  // Normalise JSON-LD into an array so we can render 1..N script tags.
  const jsonLdList = jsonLd
    ? (Array.isArray(jsonLd) ? jsonLd : [jsonLd])
    : [];
  const pathKey = canonical ? canonical.replace(SITE_URL, '') || '/' : '/';
  const jsonLdKey = JSON.stringify(jsonLdList);
  useEffect(() => {
    if (!metadata) return;
    syncHeadJsonLd(pathKey, jsonLdList);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathKey, jsonLdKey]);

  if (!metadata) return null;

  // Titre/description de la CARTE de partage. Par défaut ils reprennent
  // ceux de la page — c'est le bon comportement pour la quasi-totalité des
  // pages. Une page peut les dissocier (`ogTitle` / `ogDescription`) quand
  // l'onglet et le fil LinkedIn n'ont pas le même travail à faire : le
  // <title> porte le suffixe « | Archer » et des mots-clés pour Google, la
  // carte gagne à être plus courte et plus frontale.
  const socialTitle = ogTitle || title;
  const socialDescription = ogDescription || description;

  // Two-letter <html lang> derived from the OpenGraph locale so a page
  // localised in fr_BE renders as lang="fr" (SR-friendly + matches
  // Google's language detection).
  const htmlLang = String(locale || 'fr_BE').slice(0, 2).toLowerCase();

  // Hreflang alternates : la table de la page, sinon fr-BE seul (cf.
  // alternatesFor dans metadata.js, même règle que le manifeste serveur).
  const altMap = alternatesFor(metadata);

  return (
    <Helmet>
      {/* Sets <html lang="fr|nl|en"> so accessibility tools + Google
          detect the right language on this SPA render. */}
      <html lang={htmlLang} />

      <title>{title}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={canonical} />

      {/* Robots */}
      {noindex && <meta name="robots" content="noindex, nofollow" />}

      {/* Open Graph */}
      <meta property="og:title" content={socialTitle} />
      <meta property="og:description" content={socialDescription} />
      <meta property="og:url" content={canonical} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:type" content={ogType || 'website'} />
      <meta property="og:locale" content={locale || 'fr_BE'} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={socialTitle} />
      <meta name="twitter:description" content={socialDescription} />
      <meta name="twitter:image" content={ogImage} />

      {/* Article-specific (blog posts) */}
      {article && <meta property="article:published_time" content={article.publishedTime} />}
      {article && <meta property="article:modified_time" content={article.modifiedTime} />}
      {article && <meta property="article:author" content={article.author} />}
      {article?.section && <meta property="article:section" content={article.section} />}

      {/* Hreflang — une entrée par langue déclarée (fr-BE seul aujourd'hui)
          + x-default sur la canonique. */}
      {SUPPORTED_LANGS.map((l) => (
        altMap[l] ? (
          <link
            key={l}
            rel="alternate"
            hrefLang={HREFLANG_MAP[l] || l}
            href={altMap[l]}
          />
        ) : null
      ))}
      <link rel="alternate" hrefLang="x-default" href={canonical} />

      {/* Le JSON-LD n'est PAS rendu ici : cf. syncHeadJsonLd plus haut. */}
    </Helmet>
  );
}


// Back-compat: some pages may still import { SITE_URL } from PageHead.
export { SITE_URL };
